<template>
  <LoadingSpinner v-show="!stripeMounted" />
  <LightBox v-show="stripeMounted">
    <div id="payment-element" />
    <PayButton :price="total" :loading="btnLoading" @click="submit" />
  </LightBox>
</template>

<script setup lang="ts">
import { snackbarInjectionKey } from '~/assets/injection-keys';
import type { VisaForm } from '~/types';
import { calcTotalPrice } from '~/helpers/price';
import { cancelPaymentIntent, createPaymentIntent } from '~/helpers/http';
import type { Appearance, StripeElements } from '@stripe/stripe-js';

const snackbar = inject(snackbarInjectionKey)!;

const props = defineProps<{
  form: VisaForm;
}>();

const { stripe } = useStripe();
let elements: StripeElements;
const appearance: Appearance = { theme: 'stripe' };
const stripeMounted = ref(false);
const btnLoading = ref(false);
let paymentIntentId = '';

const { currency } = useBranch();
const { buildUrl, buildSuccessRoute } = useRouting();

const total = computed(() => calcTotalPrice(props.form, currency));

watch(stripe, mountStripe);

onUnmounted(() => {
  if (!paymentIntentId || btnLoading.value) return;
  cancelPaymentIntent(paymentIntentId);
});

async function mountStripe() {
  if (!stripe.value) throw new Error('Stripe not available');
  const payload = { form: props.form, currency };
  const { clientSecret, id } = (await createPaymentIntent(payload)) || {};
  if (!clientSecret) throw new Error('Failed getting client secret');
  paymentIntentId = id;
  elements = stripe.value.elements({ appearance, clientSecret });
  const paymentElement = elements.create("payment", { layout: "tabs" });
  paymentElement.mount("#payment-element");
  stripeMounted.value = true;
}

async function submit() {
  btnLoading.value = true;
  const route = buildSuccessRoute(props.form.id, props.form.product.country, 'stripe');
  const { error } = await stripe.value!.confirmPayment({
    elements,
    confirmParams: {
      return_url: buildUrl(route),
      receipt_email: props.form.client.email
    }
  });
  btnLoading.value = false;
  snackbar.value!.showError(error.type == "card_error" || error.type == 'validation_error'
    ? error.message : 'unexpected_payment_error'
  );
}
</script>